import {
  Component,
  computed,
  input,
  output,
  signal,
  ChangeDetectionStrategy,
  ElementRef,
  viewChildren,
} from '@angular/core';

export type M3ConnectedButtonSize = 'xs' | 'sm' | 'default' | 'lg' | 'xl';
export type M3ConnectedButtonVariant = 'round' | 'square';

export interface ConnectedButtonOption<T = string> {
  value: T;
  label: string;
  icon?: string;
  selectedIcon?: string;
}

@Component({
  selector: 'app-m3-connected-button-group',
  standalone: true,
  template: `
    <div
      class="m3-connected-button-group"
      [class]="'size-' + size() + ' variant-' + variant()"
      role="radiogroup"
      [attr.aria-label]="ariaLabel()"
    >
      @for (
        option of options();
        track option.value;
        let i = $index;
        let first = $first;
        let last = $last
      ) {
        <button
          #segmentButton
          type="button"
          class="segment"
          [class.selected]="value() === option.value"
          [class.first]="first"
          [class.last]="last"
          [class.pressing]="pressingIndex() === i"
          role="radio"
          [attr.aria-checked]="value() === option.value"
          [attr.tabindex]="isTabStop(option.value, first) ? 0 : -1"
          (click)="selectOption(option.value)"
          (mousedown)="handleMouseDown(i)"
          (mouseup)="handleMouseUp()"
          (mouseleave)="handleMouseLeave()"
          (touchstart)="handleTouchStart(i)"
          (touchend)="handleTouchEnd()"
          (keydown)="handleKeydown($event, i)"
        >
          <span class="state-layer">
            @if (
              value() === option.value &&
              (option.selectedIcon || showCheckOnSelected())
            ) {
              <span class="material-symbols-outlined" aria-hidden="true">{{
                option.selectedIcon ?? 'check'
              }}</span>
            } @else if (option.icon) {
              <span class="material-symbols-outlined" aria-hidden="true">{{
                option.icon
              }}</span>
            }
            <span class="label">{{ option.label }}</span>
          </span>
        </button>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: inline-block;
      }

      .m3-connected-button-group {
        display: flex;
        flex-direction: row;
        align-items: center;
        padding: 0;
        gap: 2px;
        border-radius: 16px;
      }

      .segment {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        padding: 0;
        min-width: 48px;
        border: none;
        cursor: pointer;
        position: relative;
        flex: 1 1 0;
        transition:
          background-color var(--md-sys-motion-standard-effects-fast),
          border-radius var(--md-comp-button-motion-shape-morph, 200ms),
          transform 150ms;

        // UNSELECTED STATE (default)
        background: var(--md-sys-color-secondary-container);
        border-radius: 4px;

        // First segment
        &.first {
          border-radius: 24px 4px 4px 24px;
        }

        // Last segment
        &.last {
          border-radius: 4px 16px 16px 4px;
        }

        // First AND last (single option)
        &.first.last {
          border-radius: 24px;
        }

        // SELECTED STATE - shape morphs to round
        &.selected {
          background: var(--md-sys-color-secondary);
          border-radius: 24px;

          .state-layer {
            color: var(--md-sys-color-on-secondary);
          }

          .material-symbols-outlined {
            color: var(--md-sys-color-on-secondary);
          }
        }

        // State layer pseudo-element
        &::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: inherit;
          opacity: 0;
          pointer-events: none;
          transition: opacity 150ms;
        }

        &:not(.selected)::before {
          background: var(--md-sys-color-on-secondary-container);
        }

        &.selected::before {
          background: var(--md-sys-color-on-secondary);
        }

        // Hover
        &:hover::before {
          opacity: 0.08;
        }

        // Pressed
        &.pressing {
          transform: scale(0.96);

          &::before {
            opacity: 0.12;
          }
        }

        // Focus
        &:focus-visible {
          outline: 2px solid var(--md-sys-color-secondary);
          outline-offset: 2px;
          z-index: 1;
        }

        &:focus:not(:focus-visible) {
          outline: none;
        }
      }

      .state-layer {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        padding: 6px 12px;
        gap: 4px;
        width: 100%;
        box-sizing: border-box;
        color: var(--md-sys-color-on-secondary-container);
      }

      .label {
        font-family: inherit;
        font-weight: 500;
        font-size: 14px;
        line-height: 20px;
        letter-spacing: 0.1px;
      }

      .material-symbols-outlined {
        width: 20px;
        height: 20px;
        font-size: 20px;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--md-sys-color-on-secondary-container);
        font-variation-settings:
          'FILL' 0,
          'wght' 400,
          'GRAD' 0,
          'opsz' 20;
      }

      // SIZE VARIANTS
      .m3-connected-button-group.size-xs .segment {
        min-height: 28px;

        .state-layer {
          padding: 4px 8px;
        }

        .label {
          font-size: 12px;
          line-height: 16px;
        }

        .material-symbols-outlined {
          width: 16px;
          height: 16px;
          font-size: 16px;
        }
      }

      .m3-connected-button-group.size-sm .segment {
        min-height: 32px;

        .state-layer {
          padding: 5px 10px;
        }

        .label {
          font-size: 13px;
          line-height: 18px;
        }

        .material-symbols-outlined {
          width: 18px;
          height: 18px;
          font-size: 18px;
        }
      }

      .m3-connected-button-group.size-default .segment {
        min-height: 40px;
      }

      .m3-connected-button-group.size-lg .segment {
        min-height: 48px;

        .state-layer {
          padding: 8px 16px;
        }

        .label {
          font-size: 15px;
          line-height: 22px;
        }

        .material-symbols-outlined {
          width: 22px;
          height: 22px;
          font-size: 22px;
        }
      }

      .m3-connected-button-group.size-xl .segment {
        min-height: 56px;

        .state-layer {
          padding: 10px 20px;
        }

        .label {
          font-size: 16px;
          line-height: 24px;
        }

        .material-symbols-outlined {
          width: 24px;
          height: 24px;
          font-size: 24px;
        }
      }

      // SQUARE VARIANT - unselected segments stay square, selected still morphs to round
      .m3-connected-button-group.variant-square {
        border-radius: 8px;

        .segment {
          border-radius: 4px;

          &.first {
            border-radius: 4px;
          }

          &.last {
            border-radius: 4px;
          }

          &.first.last {
            border-radius: 4px;
          }

          // Selected state - still morphs to round
          &.selected {
            border-radius: 24px;
          }
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class M3ConnectedButtonGroupComponent<T = string> {
  private segmentButtons = viewChildren<ElementRef>('segmentButton');

  value = input.required<T>();
  options = input.required<ConnectedButtonOption<T>[]>();
  size = input<M3ConnectedButtonSize>('default');
  variant = input<M3ConnectedButtonVariant>('round');
  ariaLabel = input<string>();
  showCheckOnSelected = input<boolean>(false);

  valueChange = output<T>();

  pressingIndex = signal<number | null>(null);

  private readonly hasSelection = computed(() =>
    this.options().some((option) => option.value === this.value())
  );

  // Radio group: only the selected option is a tab stop, the arrow keys move
  // between the options. Without a selection the first option takes the stop.
  protected isTabStop(optionValue: T, isFirst: boolean): boolean {
    return this.hasSelection() ? optionValue === this.value() : isFirst;
  }

  selectOption(optionValue: T): void {
    if (optionValue !== this.value()) {
      this.valueChange.emit(optionValue);
    }
  }

  handleKeydown(event: KeyboardEvent, currentIndex: number): void {
    const opts = this.options();
    let newIndex = currentIndex;
    let handled = false;

    switch (event.key) {
      case 'ArrowLeft':
        if (currentIndex > 0) {
          newIndex = currentIndex - 1;
          handled = true;
        }
        break;
      case 'ArrowRight':
        if (currentIndex < opts.length - 1) {
          newIndex = currentIndex + 1;
          handled = true;
        }
        break;
      case 'Home':
        newIndex = 0;
        handled = true;
        break;
      case 'End':
        newIndex = opts.length - 1;
        handled = true;
        break;
    }

    if (handled) {
      event.preventDefault();
      event.stopPropagation();

      if (newIndex !== currentIndex) {
        this.selectOption(opts[newIndex].value);
        this.focusButton(newIndex);
      }
    }
  }

  private focusButton(index: number): void {
    const buttons = this.segmentButtons();
    if (buttons[index]) {
      (buttons[index].nativeElement as HTMLButtonElement).focus();
    }
  }

  handleMouseDown(index: number): void {
    this.pressingIndex.set(index);
  }

  handleMouseUp(): void {
    this.pressingIndex.set(null);
  }

  handleMouseLeave(): void {
    if (this.pressingIndex() !== null) {
      this.pressingIndex.set(null);
    }
  }

  handleTouchStart(index: number): void {
    this.pressingIndex.set(index);
  }

  handleTouchEnd(): void {
    this.pressingIndex.set(null);
  }
}
